import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  AddEmployee,
  Employee,
  UpdateEmployee,
} from '@pflegenavi/shared/api';
import { Filter } from '../filter';

export interface GetEmployeesPhoenixResponse {
  meta: {
    current_page: number;
    next_page: number | null;
    previous_page: number | null;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  data: Employee[];
}

export interface Role {
  id: string;
  name: string;
  description: string;
  resource_types: string[];
}

export interface GetRolesResponse {
  meta: {
    total_count: number;
  };
  data: Role[];
}

export interface IEmployeesPhoenixApi extends Api {
  getNursingHomeEmployees(dataIn: {
    params: {
      nursingHomeId: string;
      order_by?: string[];
      order_directions?: Array<'asc' | 'desc'>;
      page?: number;
      page_size?: number;
      employee_states?: string[];
      // Case-insensitive match on last + first name; each word must match.
      employee_name?: string;
    };
  }): Promise<GetEmployeesPhoenixResponse>;

  getRoles(dataIn: {
    params: {
      nursingHomeId: string;
    };
  }): Promise<GetRolesResponse>;

  addEmployee(dataIn: {
    params: {
      nursingHomeId: string;
    };
    body: AddEmployee;
  }): Promise<{
    data: Employee;
  }>;

  updateEmployee(dataIn: {
    params: {
      employeeId: string;
      nursingHomeId: string;
    };
    body: UpdateEmployee;
  }): Promise<{
    data: Employee;
  }>;

  resentWelcomeEmail(dataIn: {
    params: {
      employeeId: string;
      nursingHomeId: string;
    };
  }): Promise<{
    success: boolean;
  }>;
}

export class EmployeesPhoenixApi implements IEmployeesPhoenixApi {
  baseUrl: string;

  public getNursingHomeEmployees: IEmployeesPhoenixApi['getNursingHomeEmployees'];
  public getRoles: IEmployeesPhoenixApi['getRoles'];
  public addEmployee: IEmployeesPhoenixApi['addEmployee'];
  public updateEmployee: IEmployeesPhoenixApi['updateEmployee'];
  public resentWelcomeEmail: IEmployeesPhoenixApi['resentWelcomeEmail'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.getNursingHomeEmployees = get({
      authContext,
      url: ({
        nursingHomeId,
        order_by,
        order_directions,
        page,
        page_size,
        employee_states,
        employee_name,
      }) => {
        const filter = new Filter();
        filter.fieldIfPresent('employee_state', 'in', employee_states);
        filter.fieldIfPresent('employee_name', 'ilike_and', employee_name);
        const query = filter.build();

        if (order_by) {
          order_by.forEach((field) => {
            query.append('order_by[]', field);
          });
        }
        if (order_directions) {
          order_directions.forEach((direction) => {
            query.append('order_directions[]', direction);
          });
        }
        if (page !== undefined) {
          query.append('page', page.toString());
        }
        if (page_size !== undefined) {
          query.append('page_size', page_size.toString());
        }

        const baseUrl = `${this.baseUrl}/nursing-home/${nursingHomeId}/employee`;
        const queryString = query.toString();
        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
      },
    });

    this.getRoles = get({
      authContext,
      url: ({ nursingHomeId }) => {
        const url = `${this.baseUrl}/nursing-home/${nursingHomeId}/employee/roles`;
        return url;
      },
    });

    this.addEmployee = modify({
      authContext,
      url: ({ nursingHomeId }) =>
        `${this.baseUrl}/nursing-home/${nursingHomeId}/employee`,
      method: 'POST',
    });

    this.updateEmployee = modify({
      authContext,
      url: ({ employeeId, nursingHomeId }) =>
        `${this.baseUrl}/nursing-home/${nursingHomeId}/employee/${employeeId}`,
      method: 'PATCH',
    });

    this.resentWelcomeEmail = modify({
      authContext,
      url: ({ employeeId, nursingHomeId }) =>
        `${this.baseUrl}/nursing-home/${nursingHomeId}/employee/${employeeId}/resend_welcome`,
      method: 'POST',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  get headers(): Headers {
    return new Headers({
      'content-type': 'application/json',
    });
  }
}
